// oxlint-disable typescript/no-unsafe-type-assertion

import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate';

import { Context, Event, Input } from './receipt.machine.types';
import { Actions } from './setup/receipt.machine.actions';
import { Actors } from './setup/receipt.machine.actors';
import { generateInitialContext } from './setup/receipt.machine.context';
import { Guards } from './setup/receipt.machine.guards';
import * as Inputs from './setup/receipt.machine.input';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actions: Actions,
  actors: Actors,
  guards: Guards,
});

// ─────────────────────────────────────────────────────────────────────────────

export const receiptMachine = model.createMachine({
  initial: 'fetching-receipt',
  context: (args) => generateInitialContext(args.input),
  states: {
    'fetching-receipt': {
      invoke: {
        id: 'fetchReceipt',
        src: 'fetchReceipt',
        input: Inputs.getInitialInput,
        onDone: [
          {
            target: 'fetched-claimed',
            guard: {
              type: 'checkIfReceiptAlreadyClaimedByUser',
              params: (args) => args.event.output,
            },
            actions: [{ type: 'assignReceiptData', params: (args) => args.event.output }],
          },
          {
            target: 'fetched-unclaimed',
            guard: { type: 'checkIfReceipt', params: (args) => args.event.output },
            actions: [{ type: 'assignReceiptData', params: (args) => args.event.output }],
          },
          {
            target: 'error.expired',
            guard: { type: 'checkIfExpiredReceipt', params: (args) => args.event.output },
            actions: [
              { type: 'onReceiptError', params: { userError: 'expired' } },
              { type: 'logFailedReceiptFetchDueToUserError', params: { errorType: 'expired' } },
            ],
          },
          {
            target: 'error.not-found',
            guard: { type: 'checkIfReceiptNotFound', params: (args) => args.event.output },
            actions: [
              { type: 'onReceiptError', params: { userError: 'not-found' } },
              { type: 'logFailedReceiptFetchDueToUserError', params: { errorType: 'not-found' } },
            ],
          },
          {
            target: 'error.already-claimed',
            guard: { type: 'checkIfReceiptAlreadyClaimed', params: (args) => args.event.output },
            actions: [
              { type: 'onReceiptError', params: { userError: 'already-claimed' } },
              {
                type: 'logFailedReceiptFetchDueToUserError',
                params: { errorType: 'already-claimed' },
              },
            ],
          },
          {
            target: 'error.unknown-error',
            actions: [
              { type: 'onReceiptError', params: { userError: 'unknown-error' } },
              'logFailedReceiptFetch',
            ],
          },
        ],
        onError: {
          target: 'error.unknown-error',
          actions: [
            'assignError',
            { type: 'onReceiptError', params: { userError: 'unknown-error' } },
            'logFailedReceiptFetch',
          ],
        },
      },
    },
    claiming: {
      invoke: {
        id: 'claimReceipt',
        src: 'claimReceipt',
        input: Inputs.getClaimReceiptInput,
        onDone: [
          {
            target: 'claimed',
            guard: { type: 'checkIfClaimSucceeded', params: (args) => args.event.output },
          },
          {
            target: 'error.already-claimed',
            guard: {
              type: 'checkIfReceiptAlreadyClaimedError',
              params: (args) => args.event.output,
            },
            actions: [
              { type: 'onClaimError', params: { userError: 'already-claimed' } },
              { type: 'logFailedClaimDueToUserError', params: { errorType: 'already-claimed' } },
            ],
          },
          {
            target: 'error.time-restriction',
            guard: { type: 'checkIfTimeRestrictionError', params: (args) => args.event.output },
            actions: [
              { type: 'onClaimError', params: { userError: 'time-restriction' } },
              { type: 'logFailedClaimDueToUserError', params: { errorType: 'time-restriction' } },
            ],
          },
          {
            target: 'error.not-found',
            guard: { type: 'checkIfTransactionNotFoundError', params: (args) => args.event.output },
            actions: [
              { type: 'onClaimError', params: { userError: 'not-found' } },
              { type: 'logFailedClaimDueToUserError', params: { errorType: 'not-found' } },
            ],
          },
          {
            target: 'error.unknown-error',
            actions: [
              { type: 'onClaimError', params: { userError: 'unknown-error' } },
              'logFailedClaim',
            ],
          },
        ],
        onError: {
          target: 'error.unknown-error',
          actions: [
            'assignError',
            { type: 'onClaimError', params: { userError: 'unknown-error' } },
            'logFailedClaim',
          ],
        },
      },
    },

    // ─── Auto Claim Routing ──────────────────────────────────────────

    'fetched-claimed': {
      always: [
        { target: 'claimed', guard: 'checkIfShouldAutoClaim' },
        { target: 'viewing.claimed' },
      ],
    },

    'fetched-unclaimed': {
      always: [
        { target: 'claiming', guard: 'checkIfShouldAutoClaim' },
        { target: 'viewing.unclaimed' },
      ],
    },

    // ─── Read Only Receipt State ─────────────────────────────────────

    viewing: {
      initial: 'unclaimed',
      states: {
        claimed: {
          entry: [{ type: 'logReceiptViewed', params: { isClaimed: true } }],
        },
        unclaimed: {
          entry: [{ type: 'logReceiptViewed', params: { isClaimed: false } }],
        },
      },
    },

    // ─── Final States ────────────────────────────────────────────────

    claimed: {
      type: 'final',
      entry: ['onSuccessfulClaim', 'logSuccessfulClaim'],
    },

    error: {
      initial: 'unknown-error',
      states: {
        expired: { type: 'final' },
        'time-restriction': { type: 'final' },
        'already-claimed': { type: 'final' },
        'not-found': { type: 'final' },
        'unknown-error': { type: 'final' },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type ReceiptMachineActorRef = ActorRefFrom<typeof receiptMachine>;
export type ReceiptMachineImplementations = MachineImplementationsFrom<typeof receiptMachine>;
